import { Component, OnInit, DestroyRef, effect, inject, untracked } from '@angular/core';
import { Params, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, interval } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CorporateFormsService } from '../../corporate/services/corporate-forms.service';
import { GlobalRequestDialogService } from '../../global-requests/services/global-request-dialog.service';
import { UserService } from '../../services/user.service';
import { CompaniesService } from '../../services/companies.service';
import { PermissionsService } from '../../services/permissions.service';
import { ConfirmDialogComponent } from '../../corporate/confirm-dialog/confirm-dialog.component';
import {
  NotificationVM,
  NotificationType,
  enrichNotification,
  getNotificationAvatar,
  getStatusTag,
  isPendingChangeNotification,
  taskListLink,
} from '../models/notification.model';
import { offerOtherOrgTab, openInOrgTab } from '../../shared/navigation/org-tab';
import { OrgIdentityService, OrgLensService } from '../../shared/org-identity';

interface FilterOption {
  label: string;
  value: string;
}

@Component({
    selector: 'app-notifications-page',
    templateUrl: './notifications-page.component.html',
    styleUrls: ['./notifications-page.component.scss'],
    standalone: false
})
export class NotificationsPageComponent implements OnInit {

  private readonly formsService = inject(CorporateFormsService);
  private readonly globalRequestDialog = inject(GlobalRequestDialogService);
  private readonly userService = inject(UserService);
  private readonly companiesService = inject(CompaniesService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroy = inject(DestroyRef);

  /**
   * Multi-organisation workspace (docs/multi-org). /notifications is read per user, not per
   * company, so it already holds every organisation's notifications; someone in several
   * organisations sees those of the organisations in the top bar's filter, each with its badge.
   * `notifications` is what the filter shows (every count, Mark all and Delete all act on it), and
   * `loaded` everything the server returned. Clicking one is unchanged: it never switches this
   * tab's organisation.
   */
  private readonly orgIdentity = inject(OrgIdentityService);
  readonly orgLens = inject(OrgLensService);
  readonly isMultiOrg = this.orgIdentity.isMultiOrg;
  private loaded: NotificationVM[] = [];

  private readonly refilterOnLens = effect(() => {
    this.orgLens.selectedIds();
    this.isMultiOrg();
    untracked(() => this.showLoaded());
  });

  notifications: NotificationVM[] = [];
  filteredNotifications: NotificationVM[] = [];
  // filteredNotifications split per organisation for "By organisation" (docs/multi-org).
  filteredGroups: { companyId: number; rows: NotificationVM[] }[] = [];
  isLoading = false;
  /** False until the first list arrives: the empty state waits for it instead of flashing. */
  loadedOnce = false;
  isAdmin = false;
  activeFilter = 'all';
  /** Read state chips, applied on top of the type chips. */
  readFilter: 'all' | 'unread' | 'read' = 'all';
  readonly readOptions: { label: string; value: 'all' | 'unread' | 'read' }[] = [
    { label: 'Any status', value: 'all' },
    { label: 'Unread', value: 'unread' },
    { label: 'Read', value: 'read' },
  ];
  deletingIds = new Set<number>();
  /** Companies the user belongs to, by id — "Open in <organisation>" is offered only for these. */
  private userCompanies = new Map<number, string>();

  /** The board's type segments (docs/multi-org/design/Inbox.dc.html). */
  filterOptions: FilterOption[] = [
    { label: 'All', value: 'all' },
    { label: 'Approvals', value: 'approvals' },
    { label: 'Mentions', value: 'mentions' },
    { label: 'Shares', value: 'shares' },
    { label: 'System', value: 'system' }
  ];

  /** The list in day sections ("Newest first") or one section per organisation ("By organization"). */
  sections: { key: string; label: string; companyId: number | null; rows: NotificationVM[] }[] = [];
  /** Unread per organisation the user belongs to, for the side card. */
  unreadByOrg: { id: number; name: string; n: number }[] = [];

  get unreadCount(): number {
    return this.notifications.filter(n => !n.isRead).length;
  }

  ngOnInit(): void {
    this.loadNotifications();

    this.userService.getUserCompaniesAsObs().pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(companies => this.userCompanies = new Map((companies ?? []).map(c => [c.id, c.name])));

    // Check admin status
    this.userService.getCurrentUser(false).pipe(
      catchError(() => of(null))
    ).subscribe(user => {
      if (user) {
        this.isAdmin = this.permissionsService.isCorporateAdmin();
      }
    });

    // Poll for updates every 30 seconds on the page
    interval(30000).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(() => this.loadNotifications());
  }

  private loadNotifications(): void {
    this.formsService.getAllNotifications().pipe(
      catchError(err => {
        console.error('Error loading notifications:', err);
        return of([]);
      })
    ).subscribe(notes => {
      this.loaded = notes.map(n => enrichNotification(n));
      this.loadedOnce = true;
      this.showLoaded();
    });
  }

  /**
   * The organisation a notification is shown under. One with no company (a system message, say)
   * counts as this tab's, so the filter never hides it from everyone.
   */
  orgOf(note: NotificationVM): number | null {
    return note.companyId || this.companiesService.managedCompanyId || null;
  }

  private showLoaded(): void {
    this.notifications = this.isMultiOrg()
      ? this.orgLens.filter(this.loaded, n => this.orgOf(n))
      : [...this.loaded];
    this.applyFilter();
  }

  setFilter(value: string): void {
    this.activeFilter = value;
    this.applyFilter();
  }

  setReadFilter(value: 'all' | 'unread' | 'read'): void {
    this.readFilter = value;
    this.applyFilter();
  }

  getReadCount(value: 'all' | 'unread' | 'read'): number {
    if (value === 'all') return this.notifications.length;
    return this.notifications.filter(n => value === 'unread' ? !n.isRead : n.isRead).length;
  }

  private applyFilter(): void {
    this.applyTypeFilter();
    if (this.readFilter !== 'all') {
      const unread = this.readFilter === 'unread';
      this.filteredNotifications = this.filteredNotifications.filter(n => unread ? !n.isRead : !!n.isRead);
    }
    this.filteredGroups = this.orgLens.groupRows(this.filteredNotifications, n => this.orgOf(n));
    this.buildSections();
    this.buildUnreadByOrg();
  }

  private applyTypeFilter(): void {
    this.filteredNotifications = this.activeFilter === 'all'
      ? [...this.notifications]
      : this.notifications.filter(n => this.categoryOf(n) === this.activeFilter);
  }

  getFilterCount(value: string): number {
    if (value === 'all') return this.notifications.length;
    return this.notifications.filter(n => this.categoryOf(n) === value).length;
  }

  /**
   * The board's four kinds. Approvals: anything waiting on a decision (a request, a leave request, a
   * proposed change). Mentions: tags and replies. Shares: a client or document shared. The rest is
   * System, decisions on the reader's own requests included.
   */
  categoryOf(note: NotificationVM): 'approvals' | 'mentions' | 'shares' | 'system' {
    const t = note.type || NotificationType.System;
    if (this.needsDecision(note) || t === NotificationType.Approval || t === NotificationType.LeaveRequest
      || t === NotificationType.PendingChangeRequest) return 'approvals';
    if (t === NotificationType.MeetingMention || t === NotificationType.Comment) return 'mentions';
    if (/\bshar(ed|ing)\b|\bshare request\b/i.test(`${note.title ?? ''} ${note.message ?? ''}`)) return 'shares';
    return 'system';
  }

  /** The board's icon per kind of notification. */
  iconOf(note: NotificationVM): string {
    const t = note.type || NotificationType.System;
    if (this.categoryOf(note) === 'approvals') return 'approval';
    switch (t) {
      case NotificationType.MeetingMention: return 'alternate_email';
      case NotificationType.Comment: return 'chat';
      case NotificationType.TaskList: return 'checklist';
      case NotificationType.LeaveApproved: return 'check_circle';
      case NotificationType.LeaveRejected: return 'cancel';
      case NotificationType.PendingChangeDecision: return 'rule';
      case NotificationType.Reaction: return 'favorite';
    }
    return this.categoryOf(note) === 'shares' ? 'share' : 'notifications';
  }

  /** The line in bold: the subject the server set, or the message itself on older rows. */
  titleOf(note: NotificationVM): string {
    return note.title?.trim() || note.message;
  }

  /** The grey line under it: the message when a subject was set, else the client the note is about. */
  detailOf(note: NotificationVM): string {
    return note.title?.trim() ? note.message : '';
  }

  /** Where a click goes, as the board says it: "Approve in PF" for a decision, "Opens here" otherwise. */
  whereOf(note: NotificationVM): { label: string; cls: string } | null {
    if (this.needsDecision(note) || note.leaveRequestId != null && note.type === NotificationType.LeaveRequest) {
      const org = this.orgIdentity.find(this.orgOf(note));
      return { label: `Approve in ${org?.initials ?? 'its organization'}`, cls: 'ws-ok-t' };
    }
    return this.isClickable(note) ? { label: 'Opens here', cls: '' } : null;
  }

  isClickable(note: NotificationVM): boolean {
    return !!(note.meetingNoteId || note.rootMeetingNoteId || note.leaveRequestId != null
      || note.globalRequestId != null || taskListLink(note) || isPendingChangeNotification(note));
  }

  /** HH:mm today and yesterday, the date before that. */
  timeFormat(note: NotificationVM): string {
    return this.daysAgo(note.createdAt) <= 1 ? 'HH:mm' : 'dd/MM';
  }

  private daysAgo(iso: string): number {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 99;
    const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    return Math.round((start(new Date()) - start(d)) / 86_400_000);
  }

  /** "Newest first": Today, Yesterday, Earlier. "By organization": one section per organisation. */
  private buildSections(): void {
    const rows = [...this.filteredNotifications].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    if (this.isMultiOrg() && this.orgLens.groupByOrg()) {
      this.sections = this.orgLens.groupRows(rows, n => this.orgOf(n)).map(g => ({
        key: `org-${g.companyId}`,
        label: this.orgIdentity.find(g.companyId)?.name ?? this.userCompanies.get(g.companyId) ?? 'Other',
        companyId: g.companyId,
        rows: g.rows,
      }));
      return;
    }
    const buckets: { key: string; label: string; companyId: null; rows: NotificationVM[] }[] = [
      { key: 'today', label: 'Today', companyId: null, rows: [] },
      { key: 'yesterday', label: 'Yesterday', companyId: null, rows: [] },
      { key: 'earlier', label: 'Earlier', companyId: null, rows: [] },
    ];
    for (const n of rows) {
      const age = this.daysAgo(n.createdAt);
      buckets[age <= 0 ? 0 : age === 1 ? 1 : 2].rows.push(n);
    }
    this.sections = buckets.filter(b => b.rows.length);
  }

  /** Every organisation the user is in, with its unread count among everything loaded. */
  private buildUnreadByOrg(): void {
    // Before the list arrives the counts are unknown, not zero.
    if (!this.loadedOnce) { this.unreadByOrg = []; return; }
    const counts = new Map<number, number>();
    for (const n of this.loaded) {
      const id = this.orgOf(n);
      if (id && !n.isRead) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    this.unreadByOrg = this.orgIdentity.orgs()
      .map(o => ({ id: o.id, name: o.name, n: counts.get(o.id) ?? 0 }))
      .sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
  }

  /** "Newest first" / "By organization". */
  setGroupByOrg(on: boolean): void {
    this.orgLens.setGroupByOrg(on);
    this.applyFilter();
  }

  trackBySection(_: number, s: { key: string }): string {
    return s.key;
  }

  getAvatarConfig(note: NotificationVM) {
    return getNotificationAvatar(note.type || NotificationType.System);
  }

  getStatusTag(note: NotificationVM) {
    return getStatusTag(note.type || NotificationType.System);
  }

  highlightMessage(message: string): SafeHtml {
    if (!message) return '';

    let html = this.escapeHtml(message);

    // Bold names at the start
    html = html.replace(/^([A-Z][a-z]+\s+[A-Z][a-z]+)/, '<strong>$1</strong>');
    html = html.replace(/Leave for\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/, 'Leave for <strong>$1</strong>');
    html = html.replace(/from\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/, 'from <strong>$1</strong>');

    // Highlight dates
    html = html.replace(/\(([^)]+\d{4}[^)]*)\)/g, '(<span class="highlight">$1</span>)');
    html = html.replace(
      /from\s+(\w+\s+\d{1,2}(?:\s+\d{4})?)\s+to\s+(\w+\s+\d{1,2}(?:\s+\d{4})?)/,
      'from <span class="highlight">$1</span> to <span class="highlight">$2</span>'
    );

    // Highlight note titles
    html = html.replace(/the note '([^']+)'/g, 'the note \'<span class="highlight">$1</span>\'');

    // Style rejection reason
    html = html.replace(/Reason:\s*(.+)$/, '<em class="notif-reason">Reason: $1</em>');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  onMarkAsRead(note: NotificationVM, index: number): void {
    this.formsService.updateNotificationBasedOnID(note.id).subscribe(() => {
      const actual = this.notifications.find(n => n.id === note.id);
      if (actual) actual.isRead = true;
      this.applyFilter();
    });
  }

  /**
   * Something waiting on the reader's decision: a global request to approve, or a pending change.
   * The row then carries a "Review" button; it runs the same path as clicking the row, which opens
   * the decision dialog (or the queue, or the offer to open it in its own organisation).
   */
  needsDecision(note: NotificationVM): boolean {
    if (note.globalRequestId != null) return !(note.message || '').toLowerCase().startsWith('your');
    return isPendingChangeNotification(note);
  }

  onNotificationClick(note: NotificationVM): void {
    // Mark as read
    if (!note.isRead) {
      this.formsService.updateNotificationBasedOnID(note.id).subscribe(() => {
        const actual = this.notifications.find(n => n.id === note.id);
        if (actual) actual.isRead = true;
        this.applyFilter();
      });
    }

    if (note.globalRequestId != null) {
      // "Your request was approved/rejected" is a status update for the requester — nothing to
      // action, so it opens their own list. An approval request opens the dialog, where it can be
      // approved or declined outright rather than hunting for the row in the inbox.
      const isRequesterUpdate = (note.message || '').toLowerCase().startsWith('your');
      if (isRequesterUpdate) {
        const urlTree = this.router.createUrlTree(['/global-requests/my-requests']);
        window.open(this.router.serializeUrl(urlTree), '_blank');
        return;
      }

      this.globalRequestDialog.openRequest(note.globalRequestId)
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe(result => {
          // A decided request is no longer pending, so refresh the list behind the dialog.
          if (result) this.loadNotifications();
        });
      return;
    }

    // A task list opens on its client's Task Lists step, in this tab's organisation.
    const taskList = taskListLink(note);
    if (taskList) {
      openInOrgTab(this.router, taskList.path, taskList.queryParams, null);
      return;
    }

    // Notes open in this tab's organisation: the thread loads under its own company wherever it
    // opens. Approval queues only hold their own organisation's requests, so one from another
    // organisation is offered as "Open in <organisation>" rather than jumped to.
    const other = note.companyId && note.companyId !== this.companiesService.managedCompanyId
      && this.userCompanies.has(note.companyId) ? note.companyId : null;
    if (note.meetingNoteId || note.rootMeetingNoteId) {
      // noteId is the thread (the page selects threads); targetNoteId the reply to scroll to.
      const root = note.rootMeetingNoteId || note.meetingNoteId;
      openInOrgTab(this.router, '/tagged-notes', {
        noteId: root,
        formId: note.submittedFormId,
        ...(note.meetingNoteId && note.meetingNoteId !== root ? { targetNoteId: note.meetingNoteId } : {}),
      }, null);
    } else if (isPendingChangeNotification(note)) {
      this.openQueue(other, 'This change is waiting for approval', '/admin/pending-changes', null);
    } else if (note.leaveRequestId != null) {
      this.openQueue(other, 'This leave request is', '/hr/leave/pending', { requestId: note.leaveRequestId });
    }
  }

  /** An approval queue: this tab's own in a new tab, or another organisation's behind an explicit offer. */
  private openQueue(otherCompanyId: number | null, what: string, path: string, queryParams: Params | null): void {
    if (!otherCompanyId) {
      openInOrgTab(this.router, path, queryParams, null);
      return;
    }
    const org = this.userCompanies.get(otherCompanyId);
    offerOtherOrgTab(this.snackBar, this.router, {
      message: `${what} in ${org || 'another organisation'}.`,
      orgName: org, path, queryParams, companyId: otherCompanyId,
    });
  }

  onMarkAllRead(): void {
    this.formsService.readAllNotifications().subscribe(() => {
      // The server marks every organisation's, shown or not.
      this.loaded.forEach(n => n.isRead = true);
      this.notifications.forEach(n => n.isRead = true);
      this.applyFilter();
      this.snackBar.open('All notifications marked as read', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
    });
  }

  onDelete(note: NotificationVM, index: number): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Notification',
        text: 'Are you sure you want to delete this notification?',
        subText: 'Yes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deletingIds.add(note.id);
        this.isLoading = true;

        this.formsService.deleteNotificationBasedOnID(note.id).subscribe({
          next: () => {
            setTimeout(() => {
              this.notifications = this.notifications.filter(n => n.id !== note.id);
              this.loaded = this.loaded.filter(n => n.id !== note.id);
              this.deletingIds.delete(note.id);
              this.applyFilter();
              this.isLoading = false;

              this.snackBar.open('Notification deleted', 'Close', {
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top'
              });
            }, 300);
          },
          error: (error) => {
            this.deletingIds.delete(note.id);
            this.isLoading = false;
            this.snackBar.open(`Error: ${error.message}`, 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          }
        });
      }
    });
  }

  onDeleteAll(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete All Notifications',
        text: 'Are you sure you want to delete all notifications? This cannot be undone.',
        subText: 'Delete All'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.snackBar.open('Deleting all notifications...', '', { duration: 2000 });

        const ids = this.notifications.map(n => n.id);
        this.deleteSequential(ids, 0);
      }
    });
  }

  private deleteSequential(ids: number[], index: number): void {
    if (index >= ids.length) {
      // Only the shown ones were deleted: another organisation's stay for when the filter widens.
      const deleted = new Set(ids);
      this.loaded = this.loaded.filter(n => !deleted.has(n.id));
      this.notifications = [];
      this.filteredNotifications = [];
      this.filteredGroups = [];
      this.isLoading = false;
      this.snackBar.open('All notifications deleted', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      return;
    }

    this.formsService.deleteNotificationBasedOnID(ids[index]).pipe(
      catchError(() => of(null))
    ).subscribe(() => {
      this.deleteSequential(ids, index + 1);
    });
  }

  onViewLeave(note: NotificationVM): void {
    this.router.navigate(['/hr/leave/pending'], {
      queryParams: { requestId: note.leaveRequestId }
    });
  }

  trackByNotifId(index: number, item: NotificationVM): number {
    return item.id;
  }
}
